import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import formIcon from "../../assets/icons/form.png";
import groupIcon from '../../assets/icons/group.svg'
import DownloadCard from "../DownloadCard";
import axiosInstance from "../../services/axios";
import EmptyState from "../EmptyState";

const TaxPayment = () => {
  const { t, i18n } = useTranslation();
  const [images, setImages] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [houseTaxDoc, setHouseTaxDoc] = useState(null);
  const [waterTaxDoc, setWaterTaxDoc] = useState(null);

  const getSecureUrl = (url) => {
    if (!url) return url;
    if (typeof url === 'string' && url.includes('/media/')) {
      const mediaIndex = url.indexOf('/media/');
      return url.substring(mediaIndex);
    }
    if (window.location.protocol === 'https:' && url.startsWith('http:')) {
      return url.replace(/^http:/, 'https:');
    }
    return url;
  };

  useEffect(() => {
    const fetchTaxPaymentImages = async () => {
      try {
        setLoading(true);
        const language = i18n.language === 'mr' ? 'Marathi' : 'English';
        const response = await axiosInstance.get('/tax-payments/', {
          params: { language }
        });
        
        const data = response.data;
        console.log('Tax payment API response:', data);
        if (data && data.length > 0) {
          const mappedImages = data.map(item => ({ ...item, src: item.image }));
          setImages(mappedImages);
        }
      } catch (err) {
        console.error('Error fetching tax payment images:', err);
        // Keep using static fallback image
      } finally {
        setLoading(false);
      }
    };

    const fetchDocuments = async () => {
      try {
        const [houseRes, waterRes] = await Promise.allSettled([
          axiosInstance.get('/downloadable-documents/by-category/household_income/'),
          axiosInstance.get('/downloadable-documents/by-category/water_amount/')
        ]);
        if (houseRes.status === 'fulfilled' && houseRes.value?.data) {
          const houseData = Array.isArray(houseRes.value.data) ? houseRes.value.data[0] : houseRes.value.data;
          if (houseData && (houseData.pdf_file || houseData.file_url) && !houseData.error) {
            setHouseTaxDoc(houseData);
          }
        }
        if (waterRes.status === 'fulfilled' && waterRes.value?.data) {
          const waterData = Array.isArray(waterRes.value.data) ? waterRes.value.data[0] : waterRes.value.data;
          if (waterData && (waterData.pdf_file || waterData.file_url) && !waterData.error) {
            setWaterTaxDoc(waterData);
          }
        }
      } catch (err) {
        console.error('Error fetching tax payment documents:', err);
      }
    };
    
    fetchTaxPaymentImages();
    fetchDocuments();
  }, [i18n.language]);
  
  return (
    <div id="tax-payments" className="border rounded-md p-4 bg-white flex flex-col gap-2">
      {/* title */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-3 items-center">
          <img
            className="w-10 h-10 relative"
            src={formIcon}
            alt="Form Icon"
          />
          <span className="font-semibold w-full text-2xl max-sm:text-xl h-fit m-0">
            {t('taxPayment.title') || 'कर भरणा'}
          </span>
        </div>
        <p className="max-w-4xl leading-5">
          {t('taxPayment.description') || 'कृपया खालील QR कोड स्कॅन करून संबंधित कर.'}
        </p>

        {images.length > 0 ? (<div className="flex max-md:flex-col gap-10 max-md:justify-center items-center">
          {images.map((item, index) => (
            <div key={item.id || index} className="flex flex-col items-center gap-3 w-full max-w-72">
              <img
                src={item.image || item.src}
                alt={item.title || t('taxPayment.qrCodeAlt') || 'कर भरणीसाठी QR कोड'}
                className="w-full h-auto"
                loading="lazy"
              />
              <button className="bg-[var(--color-primary)] text-white py-2 px-4 rounded-md w-full flex items-center justify-center gap-2">
                <img loading="lazy" src={groupIcon} alt="icon" className="w-4 h-4" />
                {i18n.language === 'en'
                  ? ('Pay Tax Online')
                  : ('ऑनलाइन घरपट्टी भरा')}
              </button>
            </div>
          ))}
        </div>) : (<EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />)}

        <div className="flex flex-col gap-4 mt-4">
          <DownloadCard
            title={houseTaxDoc?.title || "घरपट्टी रक्कम"}
            titleEn={houseTaxDoc?.title || "House Tax Amount"}
            description={houseTaxDoc?.description || "कृपया खालील PDF तपासा आणि आपल्या घरपट्टी कराची रक्कम जाणून घ्या."}
            descriptionEn={houseTaxDoc?.description || "Please check the PDF below and view your house tax amount."}
            downloadLink={houseTaxDoc ? getSecureUrl(houseTaxDoc.pdf_file || houseTaxDoc.file_url) : null}
            isDisabled={!houseTaxDoc}
            tooltipText="घरपट्टी कर अहवाल उपलब्ध नाही"
            tooltipTextEn="House tax report is not available"
            buttonLabel="Download"
            buttonLabelEn="Download"
          />

          <DownloadCard
            title={waterTaxDoc?.title || "पाणीपट्टी रक्कम"}
            titleEn={waterTaxDoc?.title || "Water Tax Amount"}
            description={waterTaxDoc?.description || "कृपया खालील PDF तपासा आणि आपल्या पाणीपट्टी कराची रक्कम जाणून घ्या."}
            descriptionEn={waterTaxDoc?.description || "Please check the PDF below and view your water tax amount."}
            downloadLink={waterTaxDoc ? getSecureUrl(waterTaxDoc.pdf_file || waterTaxDoc.file_url) : null}
            isDisabled={!waterTaxDoc}
            tooltipText="पाणीपट्टी कर अहवाल उपलब्ध नाही"
            tooltipTextEn="Water tax report is not available"
            buttonLabel="Download"
            buttonLabelEn="Download"
          />
        </div>
      </div>
    </div>
  );
};

export default TaxPayment;
